import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { CarInsuranceAdLandingDetails } from '../components/CarInsuranceAdLandingContent';
import '../components/CarInsuranceAdLandingContent.css';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '../data/contact';
import {
  findHoustonCarLanding,
  houstonCarPath,
  houstonCarQuotePath,
  relatedHoustonCarLandings,
  type HoustonCarLanding,
} from '../data/houstonCarLandings';

const HOUSTON_CAR_QUOTE_PATH = '/get-quote?type=car';
const STARTING_RATE_LINE = 'Rates starting from $39/mo';

function HoustonVehicleHero({ car }: { car: HoustonCarLanding }) {
  const quotePath = houstonCarQuotePath(car);

  return (
    <div className="car-ad-hero car-ad-hero--compact car-ad-hero--vehicle">
      <h1 className="car-ad-hero-title">{STARTING_RATE_LINE}</h1>
      <Link to={quotePath} className="btn btn-primary car-ad-hero-cta car-ad-hero-cta--emphasis">
        Click here to get your lowest {car.displayName} insurance quote
      </Link>
    </div>
  );
}

function HoustonTrustBullets() {
  return (
    <ul className="car-ad-trust-list" aria-label="Why GetSkimp">
      <li>We compare prices across multiple carriers for you.</li>
      <li>Lowest rate guaranteed — or get a $20 gift card.</li>
    </ul>
  );
}

function HoustonCarHero() {
  return (
    <div className="car-ad-hero car-ad-hero--compact">
      <h1 className="car-ad-hero-title">Car insurance in Houston — Lowest Rate Guaranteed</h1>
      <Link
        to={HOUSTON_CAR_QUOTE_PATH}
        className="btn btn-primary car-ad-hero-cta car-ad-hero-cta--emphasis"
      >
        Click here to get a quote and save $500
      </Link>
      <p className="car-ad-hero-call-line">
        Or call <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_DISPLAY}</a> for your quote
      </p>
    </div>
  );
}

function HoustonCarIndex() {
  const [openFaqExtra, setOpenFaqExtra] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Houston Car Insurance Quotes | GetSkimp';
    sessionStorage.setItem('insuranceType', 'car');
    sessionStorage.setItem('houstonAdMarket', 'car');
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="container car-ad-landing-page">
      <HoustonCarHero />
      <HoustonTrustBullets />

      <div className="car-ad-form-details-divider" aria-hidden="true">
        <span>Learn more</span>
      </div>

      <CarInsuranceAdLandingDetails />

      <div className="car-ad-landing-details">
        <section className="car-ad-section">
          <h2 className="car-ad-section-title">Houston drivers, we get it</h2>
          <p className="car-ad-body-copy">
            Commute miles, hail, flooding on the roads, and high auto rates come up on almost every
            Houston quote. We shop carriers that fit your vehicle — including Katy, Sugar Land, The
            Woodlands, Cypress, Pearland, Spring, and nearby ZIP codes — and we will tell you
            plainly what coverage you need.
          </p>
        </section>

        <section className="car-ad-section car-ad-faq-section">
          <h2 className="car-ad-section-title">Houston car questions</h2>
          <div className="car-ad-faq-list">
            <div className={`car-ad-faq-item${openFaqExtra ? ' car-ad-faq-item--open' : ''}`}>
              <button
                type="button"
                id="houston-car-lowest-quote-trigger"
                className="car-ad-faq-trigger"
                aria-expanded={openFaqExtra}
                aria-controls="houston-car-lowest-quote-panel"
                onClick={() => setOpenFaqExtra((v) => !v)}
              >
                <span>What does “Lowest Rate Guaranteed” mean?</span>
                <span
                  className={`car-ad-faq-chevron${openFaqExtra ? ' car-ad-faq-chevron--open' : ''}`}
                  aria-hidden
                />
              </button>
              {openFaqExtra && (
                <div
                  id="houston-car-lowest-quote-panel"
                  role="region"
                  aria-labelledby="houston-car-lowest-quote-trigger"
                  className="car-ad-faq-panel"
                >
                  <p>
                    We compare rates from the carriers GetSkimp works with and present the most
                    competitive options for your vehicle and profile. If we cannot beat a
                    qualifying competing quote you share with us, you receive a $20 gift card of
                    your choosing. Ask us for current gift card options and eligibility
                    details.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function HoustonCarDetailPage({ car }: { car: HoustonCarLanding }) {
  const [openFaqExtra, setOpenFaqExtra] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${car.displayName} Insurance in Houston, TX | GetSkimp`;
    sessionStorage.setItem('insuranceType', 'car');
    sessionStorage.setItem('houstonAdMake', car.makeSlug);
    sessionStorage.setItem('houstonAdModel', car.modelSlug);
    return () => {
      document.title = previousTitle;
    };
  }, [car]);

  return (
    <div className="container car-ad-landing-page">
      <HoustonVehicleHero car={car} />
      <HoustonTrustBullets />

      <div className="car-ad-form-details-divider" aria-hidden="true">
        <span>Learn more</span>
      </div>

      <CarInsuranceAdLandingDetails />

      <div className="car-ad-landing-details">
        <section className="car-ad-section car-ad-faq-section">
          <h2 className="car-ad-section-title">Houston {car.model} questions</h2>
          <div className="car-ad-faq-list">
            <div className={`car-ad-faq-item${openFaqExtra ? ' car-ad-faq-item--open' : ''}`}>
              <button
                type="button"
                id="houston-lowest-quote-trigger"
                className="car-ad-faq-trigger"
                aria-expanded={openFaqExtra}
                aria-controls="houston-lowest-quote-panel"
                onClick={() => setOpenFaqExtra((v) => !v)}
              >
                <span>What does “Lowest Rate Guaranteed” mean?</span>
                <span
                  className={`car-ad-faq-chevron${openFaqExtra ? ' car-ad-faq-chevron--open' : ''}`}
                  aria-hidden
                />
              </button>
              {openFaqExtra && (
                <div
                  id="houston-lowest-quote-panel"
                  role="region"
                  aria-labelledby="houston-lowest-quote-trigger"
                  className="car-ad-faq-panel"
                >
                  <p>
                    We compare rates from the carriers GetSkimp works with and present the most
                    competitive options for your profile and {car.displayName}. If we cannot beat a
                    qualifying competing quote you share with us, you receive a $20 gift card of your
                    choosing. Ask us for current gift card options and eligibility details.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="car-ad-section">
          <h2 className="car-ad-section-title">Other popular Houston vehicles</h2>
          <ul className="car-ad-vehicle-grid car-ad-vehicle-grid--compact">
            {relatedHoustonCarLandings(car, 8).map((other) => (
              <li key={`${other.makeSlug}-${other.modelSlug}`}>
                <Link to={houstonCarPath(other)} className="car-ad-vehicle-card">
                  <span className="car-ad-vehicle-name">{other.displayName}</span>
                  <span className="car-ad-vehicle-price">{STARTING_RATE_LINE}</span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="car-ad-other-vehicles-cta">
            <p className="car-ad-other-vehicles-cta-copy">
              Don&apos;t see your vehicle? Get a free car insurance quote with the basics — make and
              model optional.
            </p>
            <Link to="/get-quote?type=car" className="btn btn-primary car-ad-hero-cta">
              Get my quote
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

function HoustonCarInsuranceLandingPage() {
  const { make, model } = useParams<{ make?: string; model?: string }>();

  if (!make && !model) {
    return <HoustonCarIndex />;
  }

  const car = findHoustonCarLanding(make, model);
  if (!car) {
    return <Navigate to="/car-insurance/houston" replace />;
  }

  return <HoustonCarDetailPage car={car} />;
}

export default HoustonCarInsuranceLandingPage;
